import {useState, useEffect, ReactElement} from 'react';
import {useTranslation} from 'react-i18next';
import {List} from '@tickchak/react-share/app';
import {TShow} from '../../hooks/useShow';
import BackArrow from '../../assets/icons/BackArrow';

export interface SelectableOption<TId extends string | number = string | number> {
    id: TId;
    text: string;
    subText?: string;
    image?: string | null;
}

interface AddAction {
    text: string;
    onClick: () => void;
}

interface SelectPopupProps<TId extends string | number> {
    show: TShow;
    title: string;
    items: SelectableOption<TId>[];
    selectedIds: TId[];
    onConfirm: (ids: TId[]) => void;
    multiSelect?: boolean;
    addAction?: AddAction;
    hideSearch?: boolean;
}

function SelectPopup<TId extends string | number>({
    show, title, items, selectedIds, onConfirm, multiSelect, addAction, hideSearch,
}: SelectPopupProps<TId>): ReactElement | null {
    const {t} = useTranslation('timetable');
    const [search, setSearch] = useState('');
    const [draftIds, setDraftIds] = useState<TId[]>(selectedIds);

    useEffect(() => {
        if (show.isOpen) setDraftIds(selectedIds);
        else setSearch('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show.isOpen]);

    if (!show.isOpen) return null;

    const filtered = search ? items.filter(i => i.text.includes(search)) : items;

    const toggleId = (id: TId) => {
        if (multiSelect) {
            setDraftIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
        } else {
            setDraftIds([id]);
        }
    };

    const handleConfirm = () => {
        onConfirm(draftIds);
        show.close();
    };

    return (
        <List
            show={show}
            data-cy="selectPop"
            position={{width: '375px', height: '432px'}}
            ariaLabel={title}
            onItemClick={item => toggleId(item.id as TId)}
        >
            <List.Header
                fixed
                title={title}
                onBack={() => show.close()}
                backIcon={<BackArrow />}
                action={addAction && {variant: 'brand', ...addAction}}
            />
            {!hideSearch && <List.Search placeholder={t('search')} onChange={setSearch} />}
            <List.Items>
                {filtered.map(item => (
                    <List.Item.Checkbox
                        key={item.id}
                        id={item.id}
                        text={item.text}
                        subText={item.subText}
                        selected={draftIds.includes(item.id as TId)}
                        avatar={item.image ? {src: item.image, alt: item.text, size: 'sm'} : undefined}
                    />
                ))}
            </List.Items>
            <List.Footer fixed>
                <List.Footer.Close text={t('close')} size="category"/>
                <List.Footer.Button
                    text={t('confirm')}
                    variant="brand"
                    size="category"
                    onClick={handleConfirm}
                />
            </List.Footer>
        </List>
    );
}

export default SelectPopup;
